import React, { useEffect, useState } from "react";
import {
  FaTimes, FaFileDownload, FaListUl, FaTable, FaPaperPlane,
  FaHistory, FaUserEdit, FaKey, FaEnvelope, FaChevronDown,
  FaEdit, FaTrash, FaExclamationTriangle,
} from "react-icons/fa";

// Small inline icon chip so explanations can show the actual icon
// the user sees in the table, instead of just describing it in words.
const IconChip = ({ icon, color, bg }) => (
  <span style={{
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    width: 22, height: 22, borderRadius: "5px", background: bg, color,
    fontSize: "11px", verticalAlign: "middle", margin: "0 4px",
  }}>
    {icon}
  </span>
);

// ── Help panel — slides in from the right, full height, closes with X ──
// Pure presentational component: parent controls visibility via `open`.
export default function HelpPanel({ open, onClose, onStartTour }) {
  // Which accordion sections are expanded. "form" starts open since
  // that's where people get stuck most often.
  const [expanded, setExpanded] = useState({ form: true });

  const toggleSection = (key) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  // Close on Escape key for convenience
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  // Collapsible section — click header to expand/collapse
  const Section = ({ id, icon, title, children }) => {
    const isOpen = !!expanded[id];
    return (
      <div style={{
        border: "1px solid #e2e8f0", borderRadius: "8px",
        marginBottom: "10px", overflow: "hidden",
      }}>
        <button
          onClick={() => toggleSection(id)}
          aria-expanded={isOpen}
          style={{
            width: "100%", display: "flex", alignItems: "center", gap: "10px",
            padding: "12px 14px", background: isOpen ? "#fff" : "#f8fafc",
            border: "none", cursor: "pointer", textAlign: "left",
          }}
        >
          <div style={{
            width: 30, height: 30, borderRadius: "7px",
            background: "#E1E9FC", color: "#305DBD",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "13px", flexShrink: 0,
          }}>
            {icon}
          </div>
          <span style={{ flex: 1, fontSize: "14px", fontWeight: 700, color: "#14161B" }}>
            {title}
          </span>
          <FaChevronDown style={{
            fontSize: "12px", color: "#9ca3af",
            transform: isOpen ? "rotate(180deg)" : "none",
            transition: "transform 0.15s ease",
          }} />
        </button>
        {isOpen && (
          <div style={{ padding: "0 14px 14px 54px", fontSize: "13px", color: "#374151", lineHeight: 1.7 }}>
            {children}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Dim overlay — clicking it closes the panel */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)",
          zIndex: 1000, animation: "help-fade-in 0.2s ease",
        }}
      />

      {/* Slide-in panel */}
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: "min(480px, 100vw)", background: "#fff",
        zIndex: 1001, boxShadow: "-8px 0 30px rgba(0,0,0,0.15)",
        display: "flex", flexDirection: "column",
        animation: "help-slide-in 0.25s ease",
      }}>
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "20px 24px", borderBottom: "1px solid #e2e8f0", flexShrink: 0,
        }}>
          <div>
            <h2 style={{ margin: "0 0 2px", fontSize: "18px", fontWeight: 700, color: "#14161B" }}>
              Help &amp; guide
            </h2>
            <p style={{ margin: 0, fontSize: "12px", color: "#9ca3af" }}>
              Everything you need to submit an antibody
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close help panel"
            style={{
              background: "#f3f4f6", border: "none", borderRadius: "8px",
              width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", color: "#374151", fontSize: "16px", flexShrink: 0,
            }}
          >
            <FaTimes />
          </button>
        </div>

        {/* Scrollable content */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>

          {onStartTour && (
            <button
              onClick={() => { onClose(); onStartTour(); }}
              style={{
                width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                padding: "10px 14px", marginBottom: "16px",
                background: "#E1E9FC", color: "#305DBD", border: "none", borderRadius: "8px",
                fontSize: "13px", fontWeight: 700, cursor: "pointer",
              }}
            >
              ▶ Show me around — replay the guided tour
            </button>
          )}

          <Section id="two-ways" icon={<FaListUl />} title="Two ways to add antibodies">
            <p style={{ margin: "0 0 10px" }}>
              Whichever way feels easier for you is completely fine — both end up in the same
              place.
            </p>
            <p style={{ margin: "0 0 10px" }}>
              <strong>Fill in the form.</strong> A good option if you're adding just one or two
              antibodies. Simply complete the fields on the Home page and click{" "}
              <strong>Add to List</strong> when you're ready. 
            </p>
            <p style={{ margin: "0 0 6px" }}>
              <strong>Upload a spreadsheet.</strong> A good option if you have several antibodies to
              add at once. Here's how it works:
            </p>
            <ol style={{ margin: "0 0 6px", paddingLeft: "18px" }}>
              <li style={{ marginBottom: "4px" }}>
                Click <strong>Download template</strong> to save a blank Excel file to your computer.
              </li>
              <li style={{ marginBottom: "4px" }}>
                Open it and fill in one row per antibody, using the column headers as a guide.
              </li>
              <li style={{ marginBottom: "4px" }}>
                Save the file, then come back here and click <strong>Upload filled template</strong>{" "}
                and upload it.
              </li>
              <li>
                We'll read the file and add each row as a draft entry for you to review below.
                Once you're ready to submit, click Submit All next to the draft entries.
              </li>
            </ol>
            <p style={{ margin: 0 }}>Both Excel (.xlsx, .xls) and CSV files work fine.</p>
          </Section>

          <Section id="form" icon={<FaFileDownload />} title="Filling in the form">
            <p style={{ margin: "0 0 10px" }}>
              Fields marked with a red <span style={{ color: "#dc2626" }}>*</span> are required, so
              please make sure those are filled in: Clone ID/Name, Isotype, Clonality, Antigen,
              Institution, Host species, Antigen species, Email, and Depositor name.
            </p>
            <p style={{ margin: "0 0 10px" }}>
              For <strong>Clone ID/Name</strong>, must be unique. 
              For easier searching, we recommend avoiding spaces where possible 
              (for example, use "MF20" instead of "MF 20"). 
              If the Clone ID appears to already be in use, we'll let you know 
              so you can review it before submitting.
            </p>
            <p style={{ margin: "0 0 10px" }}>
              For <strong>Isotype,</strong> options change based on the selected 
              <strong>Clonality</strong>. For example, when <strong>Polyclonal</strong> 
              is selected, the available isotypes include all <strong>IgG</strong> options.
            </p>
            <p style={{ margin: 0 }}>
            For <strong>Institution</strong>, start typing the full institution name 
            and select the matching option from the suggestions that appear.
            </p>
          </Section>

          <Section id="drafts-table" icon={<FaTable />} title="Your draft entries">
            <p style={{ margin: "0 0 10px" }}>
              Every antibody you add — whether through the form or a spreadsheet upload — appears in
              the <strong>Draft Entries</strong> table above the form. Nothing is sent anywhere yet
              at this stage, so there's no rush.
            </p>
            <p style={{ margin: "0 0 10px" }}>
              A few icons you'll see in that table:
            </p>
            <ul style={{ margin: "0 0 10px", paddingLeft: "0", listStyle: "none" }}>
              <li style={{ marginBottom: "8px" }}>
                <IconChip icon={<FaEdit />} color="#2b6cb0" bg="#e0edfb" />
                Edit this entry
              </li>
              <li style={{ marginBottom: "8px" }}>
                <IconChip icon={<FaTrash />} color="#dc2626" bg="#fdeaea" />
                Remove this entry
              </li>
              <li>
                <IconChip icon={<FaExclamationTriangle />} color="#b45309" bg="#fef3c7" />
                This row is missing a required field — worth opening it up and filling in the gap
                before submitting
              </li>
            </ul>
            <p style={{ margin: 0 }}>
              If you'd like a copy for your own records, click <strong>Export CSV</strong> to
              download everything currently in your draft list.
            </p>
          </Section>

          <Section id="submit" icon={<FaPaperPlane />} title="Submitting to DSHB">
            <p style={{ margin: "0 0 10px" }}>
              Once you're happy with an antibody in the form, click <strong>Add to List</strong> —
              it'll be saved into your Draft Entries, and the form will clear so you can add the
              next one whenever you're ready.
            </p>
            <p style={{ margin: 0 }}>
              When everything you'd like to send is listed in Draft Entries, click{" "}
              <strong>Submit All</strong> at the top of that table. This sends everything at once to
              DSHB for review. You'll see a confirmation right here in the app — please note we
              don't currently send a separate confirmation email, so this on-screen message is the
              best place to check that your submission went through.
            </p>
          </Section>

          <Section id="find-later" icon={<FaHistory />} title="Finding your antibodies later">
            <p style={{ margin: "0 0 10px" }}>
              Everything you've added lives under the <strong>History</strong> section:
            </p>
            <p style={{ margin: "0 0 8px" }}>
              <strong>History → Drafts</strong> — antibodies you've started but haven't submitted
              yet. These are saved automatically, even if you log out or close the browser, so
              nothing is lost. Click <strong>Edit</strong> on any entry to pick up right where you
              left off.
            </p>
            <p style={{ margin: 0 }}>
              <strong>History → Submitted</strong> — antibodies you've already sent to DSHB, grouped
              by the day you submitted them, along with where each one currently stands in review.
            </p>
          </Section>

          <Section id="account" icon={<FaUserEdit />} title="Your account">
            <p style={{ margin: 0 }}>
              Feel free to open <strong>Profile</strong> anytime you'd like to update your name or
              email — changes save as soon as you click Save Changes, no extra steps needed.
            </p>
          </Section>

          <Section id="password" icon={<FaKey />} title="Forgot your password?">
            <p style={{ margin: 0 }}>
              No worries — just use the <strong>Forgot your password?</strong> link on the login
              page to set a new one. You won't need your old password to do it.
            </p>
          </Section>

          <Section id="contact" icon={<FaEnvelope />} title="Still stuck?">
            <p style={{ margin: 0 }}>
              That's completely okay — reach out to{" "}
              <a href="mailto:dshb-deposits@uiowa.edu" style={{ color: "#305DBD", fontWeight: 700 }}>
                dshb-deposits@uiowa.edu
              </a>{" "}
              anytime and we'll be happy to help.
            </p>
          </Section>

        </div>
      </div>

      <style>{`
        @keyframes help-fade-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes help-slide-in { from { transform: translateX(100%); } to { transform: translateX(0); } }
      `}</style>
    </>
  );
}